import { useEffect, useRef, useState } from "react";
import { parseMarkdown, trimIncompleteMarkdown } from "./markdownParser";
import "./markdown-message.css";

function renderInline(nodes, keyPrefix) {
  return (nodes || []).map((node, index) => {
    const key = `${keyPrefix}-i${index}`;

    if (node.type === "strong") {
      return <strong key={key}>{renderInline(node.children, key)}</strong>;
    }
    if (node.type === "em") {
      return <em key={key}>{renderInline(node.children, key)}</em>;
    }
    if (node.type === "del") {
      return <del key={key}>{renderInline(node.children, key)}</del>;
    }
    if (node.type === "code") {
      return (
        <code className="pelagie-md-code" key={key}>
          {node.value}
        </code>
      );
    }
    if (node.type === "link") {
      if (!node.href) {
        return <span key={key}>{renderInline(node.children, key)}</span>;
      }
      return (
        <a
          key={key}
          href={node.href}
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          {renderInline(node.children, key)}
        </a>
      );
    }

    const value = node.value || "";
    if (!value.includes("\n")) {
      return <span key={key}>{value}</span>;
    }
    return value.split("\n").map((part, partIndex) => (
      <span key={`${key}-l${partIndex}`}>
        {partIndex > 0 ? <br /> : null}
        {part}
      </span>
    ));
  });
}

function renderBlocks(blocks, keyPrefix) {
  return blocks.map((block, index) => {
    const key = `${keyPrefix}-b${index}`;

    if (block.type === "heading") {
      const Tag = `h${Math.min(6, Math.max(1, block.level))}`;
      return (
        <Tag className={`pelagie-md-h${block.level}`} key={key}>
          {renderInline(block.content, key)}
        </Tag>
      );
    }

    if (block.type === "paragraph") {
      return <p key={key}>{renderInline(block.content, key)}</p>;
    }

    if (block.type === "quote") {
      return (
        <blockquote key={key}>{renderBlocks(block.content, key)}</blockquote>
      );
    }

    if (block.type === "code") {
      return (
        <pre className="pelagie-md-pre" key={key}>
          <code className={block.lang ? `language-${block.lang}` : undefined}>
            {block.value}
          </code>
        </pre>
      );
    }

    if (block.type === "hr") {
      return <hr key={key} />;
    }

    if (block.type === "table") {
      return (
        <div className="pelagie-md-table-wrap" key={key}>
          <table>
            <thead>
              <tr>
                {block.head.map((cell, cellIndex) => (
                  <th key={`${key}-h${cellIndex}`}>
                    {renderInline(cell, `${key}-h${cellIndex}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={`${key}-r${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${key}-r${rowIndex}c${cellIndex}`}>
                      {renderInline(cell, `${key}-r${rowIndex}c${cellIndex}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (block.type === "list") {
      const Tag = block.ordered ? "ol" : "ul";
      return (
        <Tag
          key={key}
          start={block.ordered && block.start && block.start !== 1 ? block.start : undefined}
        >
          {block.items.map((item, itemIndex) => (
            <li key={`${key}-i${itemIndex}`}>
              {item.checked !== null ? (
                <input
                  className="pelagie-md-check"
                  type="checkbox"
                  checked={item.checked}
                  readOnly
                />
              ) : null}
              {renderInline(item.content, `${key}-i${itemIndex}`)}
              {item.children.length
                ? renderBlocks(item.children, `${key}-i${itemIndex}`)
                : null}
            </li>
          ))}
        </Tag>
      );
    }

    return null;
  });
}

export function MarkdownMessage({ text }) {
  return (
    <div className="pelagie-md">{renderBlocks(parseMarkdown(text), "md")}</div>
  );
}

export function MarkdownTypewriter({ text, animate }) {
  const [shown, setShown] = useState(animate ? "" : text);
  const playedRef = useRef(null);

  useEffect(() => {
    if (!animate || playedRef.current === text) {
      setShown(text);
      return;
    }

    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      playedRef.current = text;
      setShown(text);
      return;
    }

    playedRef.current = text;
    const tick = 16;
    const steps = Math.max(
      1,
      Math.round(Math.min(12000, 500 + text.length * 12) / tick)
    );
    const perTick = Math.max(1, Math.ceil(text.length / steps));
    let shownChars = 0;
    setShown("");

    const id = setInterval(() => {
      shownChars += perTick;
      setShown(
        shownChars >= text.length ? text : text.slice(0, shownChars)
      );
      if (shownChars >= text.length) clearInterval(id);
    }, tick);

    return () => clearInterval(id);
  }, [text, animate]);

  return <MarkdownMessage text={animate ? trimIncompleteMarkdown(shown) : shown} />;
}
